// Panneau de propriétés : équipement (désignation, protocoles, « Relier à… ») ou liaison (type, compatibilité, source).
import { useEffect, useRef, useState } from 'react';
import { ArrowLeftRight, Link2, Plus, Trash2, X } from 'lucide-react';
import { Alerte, Badge, Bouton, Champ, ChampChoix, ChampTexte, Source } from '../../components/ui.tsx';
import { ChoixSource } from './ChoixSource.tsx';
import {
  LIBELLE_LIAISON, LIBELLE_NOEUD, PROTOCOLES_PROPOSES, TYPES_LIAISON, TYPES_NOEUD, compatibiliteEffective, declare, normaliserProtocole,
  type DescriptionSource, type Donnees, type Liaison, type Noeud, type TypeLiaison,
} from './modele.ts';
import type { Selection } from './CanevasSchema.tsx';

interface Props {
  dossierId: string;
  donnees: Donnees;
  selection: NonNullable<Selection>;
  sources: Record<string, DescriptionSource>;
  lectureSeule: boolean;
  majNoeud: (id: string, champ: Partial<Noeud>, cle: string) => void;
  majLiaison: (id: string, champ: Partial<Liaison>, cle: string) => void;
  relier: (de: string, vers: string, type: TypeLiaison) => void;
  supprimer: (s: NonNullable<Selection>) => void;
  choisir: (s: Selection) => void;
  /** Change à chaque demande explicite d'édition : place le focus sur le premier champ. 0 = ne pas déplacer le focus. */
  focusJeton?: number;
}

export function BadgeCompatibilite({ etat }: { etat: 'etayee' | 'a_verifier' }) {
  return etat === 'etayee' ? <Badge ton="succes">Étayée</Badge> : <Badge ton="ambre">À vérifier</Badge>;
}

/** Type de liaison proposé par défaut entre deux équipements : premier protocole déclaré des deux côtés. */
export function typeCommun(a: Noeud | undefined, b: Noeud | undefined): TypeLiaison {
  if (a && b) for (const t of TYPES_LIAISON) if (declare(a.protocoles, t.id) && declare(b.protocoles, t.id)) return t.id;
  return 'autre';
}

export function ProprietesSchema(p: Props) {
  if (p.selection.type === 'noeud') {
    const n = p.donnees.nodes.find((x) => x.id === p.selection.id);
    return n ? <ProprietesNoeud {...p} n={n} /> : null;
  }
  const l = p.donnees.links.find((x) => x.id === p.selection.id);
  return l ? <ProprietesLiaison {...p} l={l} /> : null;
}

function ProprietesNoeud({ n, donnees, lectureSeule, majNoeud, relier, supprimer, choisir, focusJeton }: Props & { n: Noeud }) {
  const [autre, setAutre] = useState('');
  const autres = donnees.nodes.filter((x) => x.id !== n.id);
  const [cible, setCible] = useState('');
  const [typeLien, setTypeLien] = useState<TypeLiaison | ''>('');
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { setCible(''); setTypeLien(''); }, [n.id]);
  useEffect(() => { if (focusJeton) ref.current?.querySelector<HTMLInputElement>('input')?.focus(); }, [focusJeton]);
  const cle = (champ: string) => `noeud-${n.id}-${champ}`;
  const liens = donnees.links.filter((l) => l.de === n.id || l.vers === n.id);
  const nomDe = (id: string) => donnees.nodes.find((x) => x.id === id)?.label ?? 'équipement supprimé';
  const basculer = (proto: string) => {
    const deja = n.protocoles.some((x) => normaliserProtocole(x) === normaliserProtocole(proto));
    majNoeud(n.id, { protocoles: deja ? n.protocoles.filter((x) => normaliserProtocole(x) !== normaliserProtocole(proto)) : [...n.protocoles, proto] }, `${cle('proto')}-${Date.now()}`);
  };
  const typeChoisi = typeLien || typeCommun(n, donnees.nodes.find((x) => x.id === cible));

  return (
    <div className="pile" ref={ref}>
      <Champ libelle="Désignation" value={n.label} disabled={lectureSeule} maxLength={120}
        onChange={(e) => majNoeud(n.id, { label: e.target.value }, cle('label'))} />
      <ChampChoix libelle="Type d’équipement" value={n.type} disabled={lectureSeule} options={TYPES_NOEUD.map((t) => ({ valeur: t.id, libelle: t.libelle }))}
        onChange={(e) => majNoeud(n.id, { type: e.target.value as Noeud['type'] }, `${cle('type')}-${Date.now()}`)} />
      <div className="grille-form cch-grille-2">
        <Champ libelle="Fabricant" value={n.fabricant ?? ''} disabled={lectureSeule} maxLength={120}
          onChange={(e) => majNoeud(n.id, { fabricant: e.target.value || null }, cle('fabricant'))} />
        <Champ libelle="Référence" mono value={n.reference ?? ''} disabled={lectureSeule} maxLength={120}
          onChange={(e) => majNoeud(n.id, { reference: e.target.value || null }, cle('reference'))} />
      </div>
      <Champ libelle="Zone desservie" value={n.zone ?? ''} disabled={lectureSeule} maxLength={120} placeholder="Ex. : plateau R+2, chaufferie"
        onChange={(e) => majNoeud(n.id, { zone: e.target.value || null }, cle('zone'))} />
      <fieldset className="cch-protocoles" disabled={lectureSeule}>
        <legend className="champ-libelle">Protocoles déclarés</legend>
        <div className="cch-puces">
          {PROTOCOLES_PROPOSES.map((pr) => {
            const actif = n.protocoles.some((x) => normaliserProtocole(x) === normaliserProtocole(pr));
            return <button key={pr} type="button" className="cch-puce" aria-pressed={actif} onClick={() => basculer(pr)}>{pr}</button>;
          })}
          {n.protocoles.filter((x) => !PROTOCOLES_PROPOSES.some((pr) => normaliserProtocole(pr) === normaliserProtocole(x))).map((x) => (
            <button key={x} type="button" className="cch-puce" aria-pressed onClick={() => basculer(x)} aria-label={`Retirer ${x}`}>{x}<X size={14} aria-hidden /></button>
          ))}
        </div>
        {!lectureSeule && (
          <form className="rangee cch-ajout-proto" onSubmit={(e) => { e.preventDefault(); const v = autre.trim(); if (v && n.protocoles.length < 12) { basculer(v); setAutre(''); } }}>
            <input className="saisie" value={autre} onChange={(e) => setAutre(e.target.value)} maxLength={40} placeholder="Autre protocole" aria-label="Autre protocole" />
            <Bouton compact type="submit" icone={<Plus aria-hidden />} disabled={!autre.trim()}>Ajouter</Bouton>
          </form>
        )}
        <span className="champ-aide">Une liaison n’est « étayée » que si son protocole est déclaré sur les deux équipements et qu’une source le prouve.</span>
      </fieldset>
      <ChampTexte libelle="Note" rows={2} value={n.note ?? ''} disabled={lectureSeule} maxLength={2000}
        onChange={(e) => majNoeud(n.id, { note: e.target.value || null }, cle('note'))} />

      {!lectureSeule && (
        <div className="cch-relier pile" style={{ gap: 10 }}>
          <h3 className="petit" style={{ fontWeight: 600 }}>Relier à…</h3>
          {autres.length === 0 ? <p className="petit discret">Ajoutez un deuxième équipement pour créer une liaison.</p> : (
            <>
              <div className="grille-form cch-grille-2">
                <ChampChoix libelle="Équipement" value={cible} vide="Choisir…" options={autres.map((x) => ({ valeur: x.id, libelle: `${x.label} (${LIBELLE_NOEUD[x.type]})` }))}
                  onChange={(e) => setCible(e.target.value)} />
                <ChampChoix libelle="Type de liaison" value={typeChoisi} options={TYPES_LIAISON.map((t) => ({ valeur: t.id, libelle: t.libelle }))}
                  onChange={(e) => setTypeLien(e.target.value as TypeLiaison)} />
              </div>
              <Bouton icone={<Link2 aria-hidden />} disabled={!cible} onClick={() => { relier(n.id, cible, typeChoisi); setCible(''); setTypeLien(''); }}>Relier</Bouton>
            </>
          )}
        </div>
      )}

      {liens.length > 0 && (
        <div className="pile" style={{ gap: 6 }}>
          <h3 className="petit" style={{ fontWeight: 600 }}>Liaisons de cet équipement</h3>
          <ul className="cch-liste-liens">
            {liens.map((l) => {
              const eff = compatibiliteEffective(l, donnees.nodes);
              return (
                <li key={l.id}>
                  <button type="button" className="cch-lien-btn" onClick={() => choisir({ type: 'liaison', id: l.id })}>
                    <span className={`cch-pastille cch-l-${l.type}`} aria-hidden />
                    <span>{LIBELLE_LIAISON[l.type]} — {l.de === n.id ? `vers « ${nomDe(l.vers)} »` : `depuis « ${nomDe(l.de)} »`}</span>
                    <BadgeCompatibilite etat={eff.etat} />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {!lectureSeule && (
        <Bouton variante="danger" icone={<Trash2 aria-hidden />} onClick={() => supprimer({ type: 'noeud', id: n.id })}>Supprimer l’équipement</Bouton>
      )}
    </div>
  );
}

function ProprietesLiaison({ l, donnees, dossierId, sources, lectureSeule, majLiaison, supprimer, focusJeton }: Props & { l: Liaison }) {
  const [choix, setChoix] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { if (focusJeton) ref.current?.querySelector<HTMLElement>('select')?.focus(); }, [focusJeton]);
  const eff = compatibiliteEffective(l, donnees.nodes);
  const options = donnees.nodes.map((x) => ({ valeur: x.id, libelle: `${x.label} (${LIBELLE_NOEUD[x.type]})` }));
  const src = l.source ? sources[l.source.versionId] : undefined;
  const cle = (champ: string) => `liaison-${l.id}-${champ}-${Date.now()}`;
  const a = donnees.nodes.find((x) => x.id === l.de); const b = donnees.nodes.find((x) => x.id === l.vers);

  return (
    <div className="pile" ref={ref}>
      <div className="grille-form cch-grille-2">
        <ChampChoix libelle="Depuis" value={l.de} disabled={lectureSeule} options={options}
          onChange={(e) => e.target.value !== l.vers && majLiaison(l.id, { de: e.target.value }, cle('de'))} />
        <ChampChoix libelle="Vers" value={l.vers} disabled={lectureSeule} options={options}
          onChange={(e) => e.target.value !== l.de && majLiaison(l.id, { vers: e.target.value }, cle('vers'))} />
      </div>
      {!lectureSeule && <Bouton compact icone={<ArrowLeftRight aria-hidden />} onClick={() => majLiaison(l.id, { de: l.vers, vers: l.de }, cle('sens'))}>Inverser le sens</Bouton>}
      <ChampChoix libelle="Type de liaison" value={l.type} disabled={lectureSeule}
        options={TYPES_LIAISON.map((t) => ({ valeur: t.id, libelle: t.detail ? `${t.libelle} (${t.detail})` : t.libelle }))}
        onChange={(e) => majLiaison(l.id, { type: e.target.value as TypeLiaison }, cle('type'))} />
      <Champ libelle="Libellé" value={l.libelle ?? ''} disabled={lectureSeule} maxLength={120} placeholder="Ex. : ligne KNX 1.1, boucle M-Bus compteurs"
        onChange={(e) => majLiaison(l.id, { libelle: e.target.value || null }, `liaison-${l.id}-libelle`)} />

      <fieldset className="cch-compat" disabled={lectureSeule}>
        <legend className="champ-libelle">Compatibilité</legend>
        <label className="case"><input type="radio" name={`compat-${l.id}`} checked={l.compatibilite === 'a_verifier'}
          onChange={() => majLiaison(l.id, { compatibilite: 'a_verifier' }, cle('compat'))} /> À vérifier</label>
        <label className="case"><input type="radio" name={`compat-${l.id}`} checked={l.compatibilite === 'etayee'}
          onChange={() => majLiaison(l.id, { compatibilite: 'etayee' }, cle('compat'))} /> Étayée par une source documentaire</label>
      </fieldset>
      <div className="rangee" style={{ gap: 8 }}>
        <span className="petit secondaire">État retenu :</span><BadgeCompatibilite etat={eff.etat} />
      </div>
      {eff.raison && <Alerte ton="ambre" titre="Compatibilité non étayée">{eff.raison}</Alerte>}

      <div className="pile" style={{ gap: 6 }}>
        <span className="champ-libelle">Source</span>
        {l.source ? (
          <span className="rangee" style={{ gap: 8 }}>
            <Source versionId={l.source.versionId} page={l.source.page} titre={src ? `${src.titre} (version ${src.version})` : 'Document cité'} />
            {src && src.statut === 'obsolete' && <Badge ton="ambre">Version remplacée</Badge>}
            {!lectureSeule && <Bouton compact variante="fantome" onClick={() => majLiaison(l.id, { source: null }, cle('source'))}>Retirer</Bouton>}
          </span>
        ) : <span className="petit discret">Aucune source citée.</span>}
        {!lectureSeule && <Bouton compact onClick={() => setChoix(true)}>{l.source ? 'Changer de source' : 'Choisir une source'}</Bouton>}
      </div>
      {!lectureSeule && (
        <Bouton variante="danger" icone={<Trash2 aria-hidden />} onClick={() => supprimer({ type: 'liaison', id: l.id })}>Supprimer la liaison</Bouton>
      )}
      <ChoixSource dossierId={dossierId} ouvert={choix} fermer={() => setChoix(false)}
        suggestion={[a?.reference, b?.reference].filter(Boolean)[0] ?? ''}
        choisir={(s) => majLiaison(l.id, { source: s, compatibilite: 'etayee' }, cle('source'))} />
    </div>
  );
}
